<template>
  <div :class="$style.marketingAsset" data-marketing-asset-v="3.1.3">
    <div
      v-if="data?.fields?.image?.fields?.file?.url"
      :class="[
        $style.imageWrapper,
        computedFullBleedImage && $style.fullBleedImage,
      ]"
    >
      <BasePicture
        :src="`${data?.fields?.image?.fields?.file?.url}?w=350&h=350&fit=pad`"
        :class="$style.picture"
        :img-class="$style.image"
        alt=""
        aria-hidden="true"
      />
    </div>
    <div v-if="data?.fields?.title" :class="$style.title">
      {{ data?.fields?.title }}
    </div>
    <div :class="$style.ctaWrapper">
      <div>
        <BaseButton
          v-if="data?.fields?.signInToDownload && !loggedIn"
          presentation="dark"
          size="default"
          @click="handleLogin"
        >
          {{ localizedStrings['sign-in-to-download'] || 'Sign In to Download' }}
        </BaseButton>
        <a
          v-else
          :href="computedCta?.fields?.link || ''"
          target="_blank"
          :class="$style.cta"
          rel="noopener noreferrer"
          :event-action="computedCta?.eventAction"
          :event-category="computedCta?.eventCategory"
          :event-label="computedCta?.eventLabel"
          :data-entry="data?.sys?.id"
          :data-brand="computedAssociatedBrandValue"
          :data-product="computedAssociatedProductValue"
          @click="handleClick"
        >
          {{ computedCta?.fields?.label || 'Download' }}
        </a>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  TypeDownloadableAssetWithoutUnresolvableLinksResponse,
  TypeDownloadableDocumentWithoutUnresolvableLinksResponse,
} from '~/types/contentful'

const { handleLogin } = useAuthLogin()
const emit = defineEmits(['click'])

const userStore = useUserStore()
const { loggedIn } = storeToRefs(userStore)

const { localizedStrings } = useLocalizedStrings()

// Props
const props = defineProps<{
  data:
    | TypeDownloadableAssetWithoutUnresolvableLinksResponse
    | TypeDownloadableDocumentWithoutUnresolvableLinksResponse
    | undefined
  setName?: string
}>()

// Computed
const computedFullBleedImage = computed(() => {
  const fields = props?.data?.fields as { fullBleedImage?: boolean } | undefined
  return Boolean(fields?.fullBleedImage)
})

const computedAssociatedProduct = computed(
  () => props?.data?.fields.associatedProduct,
)
const computedAssociatedProductValue = computed(
  () => computedAssociatedProduct.value?.fields?.entryTitle || '',
)
const computedAssociatedBrandValue = computed(() => {
  const fields = props?.data?.fields as
    | {
        associatedBrand?: { fields?: { value?: string } }
        brand2?: { fields?: { value?: string } }
        brand?: string
      }
    | undefined
  return (
    fields?.associatedBrand?.fields?.value ||
    fields?.brand2?.fields?.value ||
    fields?.brand ||
    ''
  )
})

const computedCta = computed(() => {
  if (!props?.data?.fields?.cta) return undefined

  return {
    eventAction: 'click',
    eventCategory: 'Download',
    eventLabel: props?.data?.fields?.cta?.fields?.link,
    ...props?.data?.fields?.cta,
  }
})

const computedResourceType = computed(() => {
  return props.data?.sys?.contentType?.sys?.id === 'downloadableDocument'
    ? 'rules & documentation'
    : 'marketing material'
})

function handleClick() {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: 'ga4.trackEvent',
    eventName: 'resource_download',
    eventParams: {
      file_name: computedCta.value?.fields?.link
        ? new URL(computedCta.value.fields.link, 'https://placeholder').pathname
        : '',
      file_extension: computedCta.value?.fields?.link
        ?.split('/')
        ?.pop()
        ?.split('.')
        ?.pop()
        ?.toLowerCase(),
      link_text: computedCta.value?.fields?.label || '',
      link_url: computedCta.value?.fields?.link || '',
      resource_type: computedResourceType.value,
      resource_name: props.data?.fields?.entryTitle,
      set_name: props.setName || '',
      associated_brand: computedAssociatedBrandValue.value,
      associated_product: computedAssociatedProductValue.value,
    },
  })

  // emit click event for parent component to handle
  emit('click')
}
</script>

<style lang="scss" module>
.marketingAsset {
  display: flex;
  flex-direction: column;
  align-items: center; // required for safari
  justify-content: stretch;
  max-width: 300px;

  padding: 16px;
  text-align: center;
}

.imageWrapper {
  aspect-ratio: 1/1;
  margin-bottom: 10px;
  padding: 16px;
}

.fullBleedImage {
  padding: 0;
}

.picture {
  height: 100%;
  width: 100%;
}

.image {
  height: 100%;
  width: 100%;
  object-fit: contain;
}

.title {
  color: var(--gray-8);
  flex: 1;
  font-family: var(--base-font-family);
  font-size: to-rem(16px);
  font-weight: bold;
  line-height: math.div(20, 16);
  margin-bottom: 10px;
  max-width: 25ch;
  margin-left: auto;
  margin-right: auto;
  text-align: center;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.ctaWrapper {
  margin-top: 14px;
  text-align: center;
}

.cta.cta {
  background: var(--purple);
  border-radius: 5px;
  border: var(--button-border);
  transition: cubic-bezier(0.16, 1, 0.29, 0.99) 0.2s;

  color: var(--white);
  font-family: var(--roboto-condensed);
  font-size: 18px;
  font-weight: bold;
  text-decoration: none;

  display: inline-flex;
  justify-content: center;

  padding: 0.5em 1.45em;
  min-width: 150px;
  outline: none; // remove browser default focus state

  &:hover,
  &:focus {
    color: var(--white);
    background: var(--purple-light);
  }

  &:active {
    color: var(--white);
    background: var(--purple-dark);
  }

  // remove browser default focus state
  &::-moz-focus-inner {
    border: none;
  }
}
</style>
